import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import * as mcquizApi from '../services/mcquizApi';

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function McQuizTestPreview() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['mcquiz-test-preview', testId],
    queryFn: () => mcquizApi.getTest(Number(testId)).then((r) => r.data),
    enabled: !!testId,
  });

  const publishMutation = useMutation({
    mutationFn: () => mcquizApi.publishTest(Number(testId)),
    onSuccess: () => {
      toast.success('Test published');
      navigate('/mcquiz/tests');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.detail || 'Publish failed'),
  });

  const test = data?.test ?? data;
  const sections: any[] = test?.sections ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !test) {
    return (
      <div className="text-center py-24 text-red-500">
        Failed to load test. <button onClick={() => navigate(-1)} className="underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Preview banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200">
            PREVIEW MODE
          </span>
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Review all questions and answers before publishing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isPending}
            className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {publishMutation.isPending ? 'Publishing...' : 'Publish Test'}
          </button>
        </div>
      </div>

      {/* Test header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{test.title}</h1>
        {test.description && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{test.description}</p>
        )}
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-300">
          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded capitalize">
            {(test.test_type || '').replace(/_/g, ' ')}
          </span>
          {test.timer_minutes && (
            <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded inline-flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5" />
              {test.timer_minutes} min
            </span>
          )}
          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded capitalize">
            {test.access_level || 'free'}
          </span>
          {test.total_marks && (
            <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
              {test.total_marks} marks
            </span>
          )}
          {test.negative_ratio !== undefined && test.negative_ratio > 0 && (
            <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
              -{test.negative_ratio} negative
            </span>
          )}
        </div>
      </div>

      {/* Sections & questions */}
      {sections.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center text-gray-500 dark:text-gray-400">
          No sections or questions in this test.
        </div>
      ) : (
        sections.map((section: any, sIdx: number) => (
          <div key={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow">
            {/* Section header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {section.title || `Section ${sIdx + 1}`}
              </h2>
              {section.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {section.description}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                {section.questions?.length ?? 0} question(s)
              </p>
            </div>

            {/* Questions */}
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {(section.questions ?? []).map((q: any, qIdx: number) => (
                <div key={q.id} className="px-6 py-5 space-y-3">
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-semibold">
                      {qIdx + 1}
                    </span>
                    <p className="text-sm text-gray-900 dark:text-white leading-relaxed flex-1">
                      {q.question_text || q.question}
                    </p>
                  </div>

                  {/* Options */}
                  {q.options && (
                    <div className="ml-10 space-y-1.5">
                      {q.options.map((opt: any, oIdx: number) => {
                        const isCorrect = opt.is_correct;
                        return (
                          <div
                            key={opt.id || oIdx}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                              isCorrect
                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700'
                                : 'bg-gray-50 dark:bg-gray-700/50'
                            }`}
                          >
                            <span className="font-medium text-gray-500 dark:text-gray-400 w-5">
                              {OPTION_LETTERS[oIdx] || oIdx + 1}.
                            </span>
                            <span
                              className={
                                isCorrect
                                  ? 'text-green-800 dark:text-green-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-200'
                              }
                            >
                              {opt.text || opt.option_text}
                            </span>
                            {isCorrect && (
                              <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400 ml-auto flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Explanation */}
                  {q.explanation && (
                    <div className="ml-10 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">
                        Explanation
                      </p>
                      <p className="text-sm text-blue-900 dark:text-blue-200">{q.explanation}</p>
                    </div>
                  )}

                  {/* Meta */}
                  <div className="ml-10 flex gap-2 text-xs text-gray-400">
                    {q.subject && <span>Subject: {q.subject}</span>}
                    {q.difficulty && (
                      <span className="capitalize">Difficulty: {q.difficulty}</span>
                    )}
                    {q.marks && <span>Marks: {q.marks}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Bottom publish */}
      <div className="flex justify-end gap-3 pb-8">
        <button
          onClick={() => navigate(`/mcquiz/tests/${testId}`)}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          Back to Editor
        </button>
        <button
          onClick={() => publishMutation.mutate()}
          disabled={publishMutation.isPending}
          className="px-5 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {publishMutation.isPending ? 'Publishing...' : 'Publish Test'}
        </button>
      </div>
    </div>
  );
}
